import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from "lucide-react";
import { arrayMove } from "@dnd-kit/sortable";
import { usePackages, type PackageRow } from "@/hooks/usePackages";

interface AttachPackagesCardProps {
  /** Attached package ids, in display order. */
  packageIds: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Menu editor card: attach packages to a published menu and order them (#932).
 * They show as a "Packages" section after the menu sections, and as options in
 * the quote form's Package select.
 */
export function AttachPackagesCard({ packageIds, onChange }: AttachPackagesCardProps) {
  const { data: allPackages, isLoading, isError } = usePackages();

  const byId = useMemo(() => new Map((allPackages ?? []).map((p) => [p.id, p])), [allPackages]);
  const available = useMemo(
    () => (allPackages ?? []).filter((p) => p.is_active && !packageIds.includes(p.id)),
    [allPackages, packageIds],
  );

  // Up/down are disabled at the ends, so the target is always in range.
  const move = (idx: number, dir: -1 | 1) => onChange(arrayMove(packageIds, idx, idx + dir));
  const remove = (id: string) => onChange(packageIds.filter((p) => p !== id));
  const add = (id: string) => onChange([...packageIds, id]);

  const hiddenAttached = packageIds.some((id) => {
    const p = byId.get(id);
    return p != null && (!p.is_published || !p.is_active);
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Packages</CardTitle>
        <CardDescription className="text-xs">
          Shown after the menu sections, in this order. Visitors can pick one on the quote form.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading packages" />
        ) : isError ? (
          <p className="text-xs text-destructive">Couldn't load packages. Attached packages are unchanged.</p>
        ) : (
          <>
            {packageIds.length > 0 && (
              <ul className="space-y-1">
                {packageIds.map((id, idx) => (
                  <AttachedRow
                    key={id}
                    pkg={byId.get(id)}
                    isFirst={idx === 0}
                    isLast={idx === packageIds.length - 1}
                    onUp={() => move(idx, -1)}
                    onDown={() => move(idx, 1)}
                    onRemove={() => remove(id)}
                  />
                ))}
              </ul>
            )}
            {hiddenAttached && (
              <p className="text-xs text-muted-foreground">
                Unpublished or inactive packages stay hidden on the public menu.
              </p>
            )}
            {(allPackages ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">No packages yet. Create one in Catalog → Packages.</p>
            ) : (
              available.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">Add a package</p>
                  <div className="flex flex-wrap gap-1">
                    {available.map((p) => (
                      <Button key={p.id} type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => add(p.id)}>
                        <Plus className="h-3 w-3 mr-1" />
                        {p.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface AttachedRowProps {
  pkg: PackageRow | undefined;
  isFirst: boolean;
  isLast: boolean;
  onUp: () => void;
  onDown: () => void;
  onRemove: () => void;
}

function AttachedRow({ pkg, isFirst, isLast, onUp, onDown, onRemove }: AttachedRowProps) {
  const name = pkg?.name ?? "Unknown package";
  return (
    <li className="flex items-center gap-1 rounded-md border px-2 py-1">
      <span className="flex-1 min-w-0 truncate text-sm">{name}</span>
      {pkg && !pkg.is_published && <Badge variant="outline" className="text-[10px]">Not published</Badge>}
      {pkg && !pkg.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={isFirst} onClick={onUp} aria-label={`Move ${name} up`}>
        <ChevronUp className="h-3 w-3" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" disabled={isLast} onClick={onDown} aria-label={`Move ${name} down`}>
        <ChevronDown className="h-3 w-3" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove} aria-label={`Remove ${name}`}>
        <Trash2 className="h-3 w-3" />
      </Button>
    </li>
  );
}
